import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FileText, Image as ImageIcon, Loader2, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface TemporaryAttachmentItem {
  id: string;
  file: File;
  error?: string;
}

interface Props {
  items: TemporaryAttachmentItem[];
  onItemsChange: (items: TemporaryAttachmentItem[]) => void;
  disabled?: boolean;
  processing?: boolean;
  className?: string;
  children: React.ReactNode;
}

const MAX_FILES = 3;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_PDF_BYTES = 15 * 1024 * 1024;
const MAX_TEXT_BYTES = 1 * 1024 * 1024;

const ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
  '.txt',
  '.md',
  '.markdown',
  '.csv',
].join(',');

const ATTACHMENTS_ENABLED = import.meta.env.VITE_TEMP_ATTACHMENTS_ENABLED !== 'false';

function fileKey(file: File): string {
  const randomId = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  return `${file.name}:${file.size}:${file.lastModified}:${randomId}`;
}

function validateFile(file: File): string | undefined {
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();

  if (type.startsWith('image/')) {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(type)) {
      return 'Unsupported image type';
    }
    return file.size > MAX_IMAGE_BYTES ? 'Image is larger than 10 MB' : undefined;
  }

  if (type === 'application/pdf' || name.endsWith('.pdf')) {
    return file.size > MAX_PDF_BYTES ? 'PDF is larger than 15 MB' : undefined;
  }

  const isText =
    type === 'text/plain' ||
    type === 'text/markdown' ||
    type === 'text/csv' ||
    name.endsWith('.txt') ||
    name.endsWith('.md') ||
    name.endsWith('.markdown') ||
    name.endsWith('.csv');

  if (isText) {
    return file.size > MAX_TEXT_BYTES ? 'Text file is larger than 1 MB' : undefined;
  }

  return 'Unsupported file type';
}

function fileIcon(file: File) {
  if ((file.type || '').startsWith('image/')) {
    return <ImageIcon className="h-3.5 w-3.5" />;
  }
  return <FileText className="h-3.5 w-3.5" />;
}

function isImageFile(file: File): boolean {
  return (file.type || '').startsWith('image/');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function fileTypeLabel(file: File): string {
  const name = file.name.toLowerCase();
  if (isImageFile(file)) return 'Image';
  if ((file.type || '').includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (name.endsWith('.csv') || file.type === 'text/csv') return 'CSV';
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'Markdown';
  return 'File';
}

function useObjectUrl(file: File, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [enabled, file]);

  return url;
}

interface AttachmentContextValue {
  items: TemporaryAttachmentItem[];
  addFiles: (files: File[]) => void;
  removeItem: (id: string) => void;
  disabled: boolean;
  processing: boolean;
  enabled: boolean;
}

const AttachmentContext = createContext<AttachmentContextValue | null>(null);

function useAttachmentContext(): AttachmentContextValue {
  const ctx = useContext(AttachmentContext);
  if (!ctx) {
    throw new Error(
      'TemporaryAttachmentPicker.Chips and .Trigger must be rendered inside <TemporaryAttachmentPicker>',
    );
  }
  return ctx;
}

/**
 * Tracks whether a horizontally scrollable element is scrolled away from its
 * start/end edges, so callers can fade the overflowing side.
 */
function useHorizontalScrollFade(deps: React.DependencyList) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const start = scrollLeft > 1;
    const end = scrollLeft + clientWidth < scrollWidth - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, ...deps]);

  return { ref, showStart: edges.start, showEnd: edges.end };
}

function buildMaskImage(showStart: boolean, showEnd: boolean): string | undefined {
  if (!showStart && !showEnd) return undefined;
  const fade = '1.25rem';
  const stops: string[] = [];
  stops.push(showStart ? 'transparent' : 'black');
  if (showStart) stops.push(`black ${fade}`);
  if (showEnd) stops.push(`black calc(100% - ${fade})`);
  stops.push(showEnd ? 'transparent' : 'black');
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

const AttachmentChip: React.FC<{
  item: TemporaryAttachmentItem;
  disabled: boolean;
  processing: boolean;
  onRemove: (id: string) => void;
}> = ({ item, disabled, processing, onRemove }) => {
  const previewUrl = useObjectUrl(item.file, isImageFile(item.file));
  const meta = item.error || `${fileTypeLabel(item.file)} · ${formatFileSize(item.file.size)}`;

  return (
    <div
      className={cn(
        'group relative flex h-14 max-w-[280px] shrink-0 items-center gap-2 overflow-hidden rounded-xl border bg-background pr-8 text-xs shadow-sm',
        item.error && 'border-destructive bg-destructive/10 text-destructive',
        processing && !item.error && 'border-primary/30 bg-primary/5',
      )}
      title={item.error || item.file.name}
    >
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden bg-muted">
        {processing && !item.error ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted-foreground/10 text-muted-foreground">
            {fileIcon(item.file)}
          </span>
        )}
      </div>
      <div className="min-w-0 py-2">
        <div className="truncate font-medium text-foreground">{item.file.name}</div>
        <div className={cn('truncate text-muted-foreground', item.error && 'text-destructive')}>
          {meta}
        </div>
      </div>
      {!processing && (
        <button
          type="button"
          aria-label={`Remove ${item.file.name}`}
          onClick={() => onRemove(item.id)}
          className="absolute right-1.5 top-1.5 rounded-full bg-background/90 p-1 text-muted-foreground shadow-sm ring-1 ring-border transition hover:text-foreground disabled:opacity-50"
          disabled={disabled}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const Chips: React.FC<{ className?: string }> = ({ className }) => {
  const { items, removeItem, disabled, processing, enabled } = useAttachmentContext();
  const { ref, showStart, showEnd } = useHorizontalScrollFade([items.length]);

  if (!enabled || items.length === 0) {
    return null;
  }

  const maskImage = buildMaskImage(showStart, showEnd);

  return (
    <div
      ref={ref}
      style={maskImage ? { WebkitMaskImage: maskImage, maskImage } : undefined}
      className={cn(
        'mb-2 flex gap-2 overflow-x-auto pb-1',
        '[scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent]',
        '[&::-webkit-scrollbar]:h-1.5',
        '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border',
        '[&::-webkit-scrollbar-track]:bg-transparent',
        className,
      )}
    >
      {items.map((item) => (
        <AttachmentChip
          key={item.id}
          item={item}
          disabled={disabled}
          processing={processing}
          onRemove={removeItem}
        />
      ))}
    </div>
  );
};

const Trigger: React.FC<{ className?: string }> = ({ className }) => {
  const { addFiles, items, disabled, processing, enabled } = useAttachmentContext();
  const inputRef = useRef<HTMLInputElement>(null);

  if (!enabled) {
    return null;
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ACCEPT}
        multiple
        onChange={(event) => {
          addFiles(Array.from(event.target.files || []));
          event.currentTarget.value = '';
        }}
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="Attach files"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || processing || items.length >= MAX_FILES}
        className={cn('h-9 w-9 shrink-0 rounded-lg', className)}
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </>
  );
};

type TemporaryAttachmentPickerComponent = React.FC<Props> & {
  Chips: React.FC<{ className?: string }>;
  Trigger: React.FC<{ className?: string }>;
};

const TemporaryAttachmentPickerBase: React.FC<Props> = ({
  items,
  onItemsChange,
  disabled = false,
  processing = false,
  className,
  children,
}) => {
  const [dropActive, setDropActive] = useState(false);
  const dragDepth = useRef(0);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!files.length || disabled || processing) return;

      const availableSlots = Math.max(0, MAX_FILES - items.length);
      const nextFiles = files.slice(0, availableSlots);
      const overflow = files.length > availableSlots;
      const nextItems = [
        ...items,
        ...nextFiles.map((file) => ({
          id: fileKey(file),
          file,
          error: validateFile(file),
        })),
      ];

      const totalBytes = nextItems.reduce((sum, item) => sum + item.file.size, 0);
      if (totalBytes > MAX_TOTAL_BYTES && nextItems.length > 0) {
        nextItems[nextItems.length - 1] = {
          ...nextItems[nextItems.length - 1],
          error: 'Total attachment size is larger than 25 MB',
        };
      }

      if (overflow && nextItems.length > 0) {
        nextItems[nextItems.length - 1] = {
          ...nextItems[nextItems.length - 1],
          error: `You can attach up to ${MAX_FILES} files`,
        };
      }

      onItemsChange(nextItems);
    },
    [disabled, items, onItemsChange, processing],
  );

  const removeItem = useCallback(
    (id: string) => {
      if (disabled || processing) return;
      onItemsChange(items.filter((item) => item.id !== id));
    },
    [disabled, items, onItemsChange, processing],
  );

  const contextValue = useMemo<AttachmentContextValue>(
    () => ({ items, addFiles, removeItem, disabled, processing, enabled: ATTACHMENTS_ENABLED }),
    [items, addFiles, removeItem, disabled, processing],
  );

  if (!ATTACHMENTS_ENABLED) {
    return (
      <AttachmentContext.Provider value={contextValue}>
        <div className={className}>{children}</div>
      </AttachmentContext.Provider>
    );
  }

  const interactive = !disabled && !processing;
  const hasFiles = (event: React.DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types || []).includes('Files');

  return (
    <AttachmentContext.Provider value={contextValue}>
      <div
        className={cn('relative', className)}
        onPaste={(event) => {
          const files = Array.from(event.clipboardData.files || []);
          if (files.length) addFiles(files);
        }}
        onDragEnter={(event) => {
          if (!interactive || !hasFiles(event)) return;
          dragDepth.current += 1;
          setDropActive(true);
        }}
        onDragOver={(event) => {
          if (!interactive || !hasFiles(event)) return;
          event.preventDefault();
        }}
        onDragLeave={() => {
          if (!interactive) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDropActive(false);
        }}
        onDrop={(event) => {
          if (!interactive) return;
          event.preventDefault();
          dragDepth.current = 0;
          setDropActive(false);
          addFiles(Array.from(event.dataTransfer.files || []));
        }}
      >
        {children}
        {dropActive && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-primary/40 bg-background/80 backdrop-blur-sm">
            <div className="flex items-center gap-3 rounded-2xl border bg-background px-4 py-3 text-sm font-medium text-foreground shadow-lg">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Paperclip className="h-4 w-4" />
              </span>
              <span>Drop files to attach</span>
            </div>
          </div>
        )}
      </div>
    </AttachmentContext.Provider>
  );
};

export const TemporaryAttachmentPicker =
  TemporaryAttachmentPickerBase as TemporaryAttachmentPickerComponent;
TemporaryAttachmentPicker.Chips = Chips;
TemporaryAttachmentPicker.Trigger = Trigger;
